import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console for developers; could also forward to a logging service.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      return (
        <div
          className="flex items-center justify-center min-h-[60vh] p-6"
          data-testid="error-boundary-fallback"
        >
          <div className="max-w-2xl w-full bg-card border rounded-lg shadow-sm p-8">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
                <p className="text-muted-foreground mb-4">
                  This page hit an unexpected error. Your data is safe — try reloading the page or
                  going back to the dashboard.
                </p>

                {error?.message && (
                  <div className="mb-4 p-3 rounded-md bg-muted text-sm font-mono text-muted-foreground break-all">
                    {error.message}
                  </div>
                )}

                {process.env.NODE_ENV === 'development' && errorInfo?.componentStack && (
                  <details className="mb-4">
                    <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                      Stack trace
                    </summary>
                    <pre className="mt-2 p-3 rounded-md bg-muted text-xs overflow-auto max-h-64">
                      {errorInfo.componentStack}
                    </pre>
                  </details>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={this.handleReset}
                    variant="default"
                    data-testid="error-boundary-retry-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try again
                  </Button>
                  <Button
                    onClick={this.handleReload}
                    variant="outline"
                    data-testid="error-boundary-reload-btn"
                  >
                    Reload page
                  </Button>
                  <Button
                    onClick={this.handleGoHome}
                    variant="ghost"
                    data-testid="error-boundary-home-btn"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Go to dashboard
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
